/** Hooks over the device's BarkStore: items for a project, the join context, reconnect flush. */

import { useQuery } from '@tanstack/react-query'
import type { BarkRecord } from '@viko/protocol'
import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { type Backend, useBackend } from '../../lib/backend.js'
import type { AggBuild, AggContext, AggRun, AggTask } from '../../lib/bark-aggregate.js'
import {
  type BarkBackend,
  BarkStore,
  barkStoreFor,
  pickTriageMachine,
} from '../../lib/bark-store.js'
import {
  useBuilds,
  useConnection,
  useMachines,
  useProject,
  useRuns,
  useTasks,
} from '../../lib/queries.js'
import { activePoll } from '../../lib/query-client.js'
import type { TaskDetail } from '../../lib/types.js'
import { createMockBarkStore } from '../../mock/bark.js'

/** The encrypted account's backend: entities, signed dispatch, project binding, sealed uploads. */
export function backendBarks(b: Backend): BarkBackend {
  return {
    entities: () => b.entities as never,
    dispatch: (kind, machineId, payload) => b.dispatch(kind, machineId, payload),
    binding: (machineId, projectId) => b.barkBinding(machineId, projectId),
    upload: async (files, scope, machineId) =>
      (await b.uploadAttachments(files, scope, machineId)) as never,
  }
}

/** Mock builds (no content keys) get an in-memory stand-in with a simulated triage agent. */
let mockFactory: ((b: Backend) => BarkStore) | undefined
const mockListeners = new Set<() => void>()
export function setMockBarkFactory(f: (b: Backend) => BarkStore) {
  mockFactory = f
  for (const l of mockListeners) l()
}
// Inline literal: production builds drop this call and tree-shake the (side-effect free) mock.
// Static on purpose: a dynamic chunk would split zod out ahead of the jitless config.
if (import.meta.env.VITE_VIKO_MOCK === '1') setMockBarkFactory(createMockBarkStore)

export function useBarkStore(): BarkStore | undefined {
  const b = useBackend()
  const ents = b.entities
  const mock = useSyncExternalStore(
    (cb) => {
      mockListeners.add(cb)
      return () => mockListeners.delete(cb)
    },
    () => mockFactory,
  )
  return useMemo(() => {
    if (ents) return barkStoreFor(ents, () => new BarkStore(backendBarks(b)))
    if (mock) return barkStoreFor(b, () => mock(b))
    return undefined
  }, [b, ents, mock])
}

export function useBarks(projectId: string) {
  const store = useBarkStore()
  const rev = useSyncExternalStore(
    store?.subscribe ?? (() => () => {}),
    store?.version ?? (() => 0),
  )
  const q = useQuery({
    queryKey: ['barks', projectId, !!store],
    queryFn: () => store!.list(projectId),
    enabled: !!store,
    // Live enough to feel real time while anything is moving; quiet otherwise (this tab's own
    // writes refetch through the store revision; other devices' within a minute).
    ...activePoll((query) =>
      (query.state.data as BarkRecord[] | undefined)?.some((b) =>
        ['queued', 'triaging', 'discussing'].includes(b.status),
      )
        ? 1500
        : 60_000,
    ),
  })
  const refetch = q.refetch
  useEffect(() => {
    if (rev) void refetch()
  }, [rev, refetch])
  // Reconnect: offline barks leave the outbox exactly once.
  const conn = useConnection()
  useEffect(() => {
    if (conn === 'live') void store?.flush()
  }, [conn, store])
  return { ...q, store }
}

export function useBark(projectId: string, barkId: string) {
  const all = useBarks(projectId)
  return { ...all, bark: all.data?.find((x) => x.id === barkId) }
}

/** Tasks and runs keyed for the aggregate join. */
export function useAggContext(projectId: string): AggContext {
  const tasks = useTasks({ projectId })
  const runs = useRuns()
  const builds = useBuilds(projectId) // outcome-polish: follow landed work into its build
  const machines = useMachines() // bark-route: names for "Routing used"
  return useMemo(() => {
    const t = new Map<string, AggTask>()
    for (const x of (tasks.data ?? []) as TaskDetail[])
      t.set(x.id, {
        id: x.id,
        title: x.title,
        status: x.status,
        runId: x.runId,
        landedAt: x.landedAt,
        blockedReason: x.blockedReason,
      })
    const r = new Map<string, AggRun>()
    for (const x of runs.data ?? [])
      r.set(x.runId, {
        runId: x.runId,
        status: String(x.status),
        title: x.title,
        activity: x.activity,
        blockedOn: x.blockedOn,
        landing: x.landing,
        latestBuild: x.latestBuild,
      })
    const bs: AggBuild[] = (builds.data ?? []).map((x) => ({
      buildId: x.buildId,
      title: x.title,
      version: x.version,
      status: x.status,
      laneId: x.laneId,
      taskIds: x.taskIds,
      createdAt: x.createdAt,
    }))
    const ms = new Map((machines.data ?? []).map((m) => [m.id, m.name] as const))
    return { tasks: t, runs: r, now: Date.now(), builds: bs, machines: ms }
  }, [tasks.data, runs.data, builds.data, machines.data])
}

/** The project's triage machine for new barks. */
export function useTriageMachine(projectId: string): string | undefined {
  const project = useProject(projectId).data
  const machines = useMachines().data ?? []
  return useMemo(() => pickTriageMachine(project, machines), [project, machines])
}
